import { useEffect, useRef, useState } from "react";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileArrowUp, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const CATS = [
  "Government Skill Development Projects", "Corporate Training",
  "Placement/Recruitment Income", "Manpower Supply Income",
  "Training Fees", "Other Income",
];

export default function Income() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [centres, setCentres] = useState([]);
  const [open, setOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]); // uploaded but not yet linked
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [f, setF] = useState({
    centre_id: "", project_name: "", client: "", invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    category: "Training Fees", description: "",
    amount_before_gst: 0, gst: 0, tds_pct: 2, amount_received: 0,
    receipt_date: "", payment_reference: "", bank_account: "", remarks: "",
  });
  const canCreate = ["admin", "accounts", "finance", "ho_approver", "mis"].includes(user?.role);
  const isMIS = user?.role === "mis";

  const load = () => api.get("/income", { params: { page, page_size: 10 } })
    .then((r) => { setItems(r.data.items || []); setTotalRecords(r.data.total || 0); });
  useEffect(() => {
    load();
    api.get("/centres").then((r) => setCentres(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/files/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPendingAttachments((cur) => [...cur, {
        file_id: r.data.id, filename: file.name,
        content_type: file.type, kind: "invoice",
      }]);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const removePending = (fid) =>
    setPendingAttachments((cur) => cur.filter((a) => a.file_id !== fid));

  const total = Number(f.amount_before_gst || 0) + Number(f.gst || 0);
  const tdsPct = Number(f.tds_pct || 0);
  const tdsAmount = Math.round(total * tdsPct) / 100;
  const netReceivable = Math.round((total - tdsAmount) * 100) / 100;
  const submit = async () => {
    if (!f.centre_id) return toast.error("Select a centre");
    if (!f.description?.trim()) return toast.error("Description is mandatory.");
    try {
      await api.post("/income", {
        ...f,
        total_invoice_amount: total,
        tds_amount: tdsAmount,
        net_receivable: netReceivable,
        attachments: pendingAttachments,
      });
      toast.success(
        isMIS
          ? "Income submitted for Centre Coordinator approval"
          : pendingAttachments.length
            ? `Income recorded with ${pendingAttachments.length} attachment(s)`
            : "Income recorded"
      );
      setOpen(false);
      setPendingAttachments([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const approveIncome = async (id) => {
    try {
      await api.post(`/income/${id}/approve`, { remarks: "" });
      toast.success("Income approved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const rejectIncome = async (id) => {
    const reason = window.prompt("Reason for rejecting this income entry?");
    if (!reason) return;
    try {
      await api.post(`/income/${id}/reject`, { remarks: reason });
      toast.info("Income rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const canActOn = (i) => {
    const st = i.approval_status;
    if (!["submitted", "in_review"].includes(st)) return false;
    if (user?.role === "admin") return true;
    return user?.role === i.next_approver_role;
  };

  return (
    <div className="p-8 space-y-6" data-testid="income-root">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Receipts</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Income</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2" data-testid="new-income-btn">
                <Plus size={14} weight="bold" /> Add Income
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">New Income Entry</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest">Centre</Label>
                  <Select value={f.centre_id} onValueChange={(v) => setF({ ...f, centre_id: v })}>
                    <SelectTrigger className="rounded-sm h-10 mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <F label="Project"><Input value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Client / Department"><Input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Category">
                  <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                    <SelectTrigger className="rounded-sm h-10 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Invoice No"><Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Invoice Date"><Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Amount before GST"><Input type="number" value={f.amount_before_gst} onChange={(e) => setF({ ...f, amount_before_gst: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" data-testid="income-amount-base" /></F>
                <F label="GST"><Input type="number" value={f.gst} onChange={(e) => setF({ ...f, gst: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" data-testid="income-gst" /></F>
                <F label="Total Invoice"><Input value={fmtINR(total)} readOnly className="rounded-sm h-10 mt-2 bg-zinc-50 tabular-nums text-right" data-testid="income-total" /></F>
                <F label="TDS %"><Input type="number" step="0.01" value={f.tds_pct} onChange={(e) => setF({ ...f, tds_pct: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" data-testid="income-tds-pct" /></F>
                <F label="TDS Amount (auto)"><Input value={fmtINR(tdsAmount)} readOnly className="rounded-sm h-10 mt-2 bg-zinc-50 tabular-nums text-right" data-testid="income-tds-amount" /></F>
                <F label="Net Receivable (auto)"><Input value={fmtINR(netReceivable)} readOnly className="rounded-sm h-10 mt-2 bg-emerald-50 border-emerald-300 tabular-nums text-right font-medium" data-testid="income-net-receivable" /></F>
                <F label="Amount Received"><Input type="number" value={f.amount_received} onChange={(e) => setF({ ...f, amount_received: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" /></F>
                <F label="Payment Reference"><Input value={f.payment_reference} onChange={(e) => setF({ ...f, payment_reference: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Bank Account"><Input value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <div className="col-span-2"><Label className="text-xs uppercase tracking-widest">Description <span className="text-rose-600">*</span></Label>
                  <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                    required data-testid="income-description"
                    className="rounded-sm mt-2" rows={2} /></div>

                {/* Invoice / receipt attachments */}
                <div className="col-span-2 border-t border-zinc-200 pt-3">
                  <Label className="text-xs uppercase tracking-widest">Invoice / Receipt</Label>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={uploadInvoice} className="hidden" data-testid="income-upload-input" />
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                      disabled={uploading} data-testid="income-upload-btn"
                      className="rounded-sm h-10 gap-2 text-xs border-zinc-300">
                      <FileArrowUp size={14} />
                      {uploading ? "Uploading…" : (pendingAttachments.length ? "Add another" : "Upload invoice / receipt")}
                    </Button>
                    <span className="text-[10px] text-zinc-500">PDF, JPG or PNG · uploaded before saving the entry</span>
                  </div>
                  {pendingAttachments.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid="income-pending-attachments">
                      {pendingAttachments.map((a) => (
                        <li key={a.file_id} className="flex items-center justify-between p-2 border border-zinc-200 rounded-sm text-xs">
                          <span className="flex items-center gap-2 truncate">
                            <Paperclip size={12} className="text-zinc-500" />
                            {a.filename}
                          </span>
                          <button type="button" onClick={() => removePending(a.file_id)}
                            className="text-rose-600 hover:text-rose-800"
                            data-testid={`income-remove-attachment-${a.file_id}`}>
                            <X size={12} weight="bold" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setOpen(false); setPendingAttachments([]); }} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} data-testid="save-income" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Save Income</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Income No</th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4">Client</th>
              <th className="text-left p-4">Invoice</th>
              <th className="text-left p-4">Category</th>
              <th className="text-right p-4">Total</th>
              <th className="text-right p-4">TDS</th>
              <th className="text-right p-4">Net Receivable</th>
              <th className="text-right p-4">Received</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Docs</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {items.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-zinc-400">No income entries yet.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`income-row-${i.income_no}`}>
                <td className="p-4 font-mono text-xs">{i.income_no}</td>
                <td className="p-4">{i.centre_code}</td>
                <td className="p-4">{i.client || "—"}</td>
                <td className="p-4 font-mono text-xs">{i.invoice_number || "—"}</td>
                <td className="p-4 text-xs">{i.category}</td>
                <td className="p-4 text-right font-medium">{fmtINR(i.total_invoice_amount)}</td>
                <td className="p-4 text-right text-zinc-600">{fmtINR(i.tds_amount)}</td>
                <td className="p-4 text-right font-medium text-emerald-700">{fmtINR(i.net_receivable || i.total_invoice_amount)}</td>
                <td className="p-4 text-right">{fmtINR(i.amount_received)}</td>
                <td className="p-4 text-xs">
                  <span className={`inline-block px-2 py-1 rounded-sm border ${
                    i.approval_status === "posted" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                    : i.approval_status === "rejected" ? "bg-rose-50 border-rose-300 text-rose-800"
                    : i.approval_status === "in_review" ? "bg-amber-50 border-amber-300 text-amber-800"
                    : "bg-zinc-100 border-zinc-300 text-zinc-700"
                  }`} data-testid={`income-status-${i.income_no}`}>
                    {i.approval_status || "posted"}
                  </span>
                  {i.next_approver_role && ["submitted","in_review"].includes(i.approval_status) && (
                    <div className="text-[10px] text-zinc-500 mt-1">→ {i.next_approver_role.replace(/_/g, " ")}</div>
                  )}
                </td>
                <td className="p-4">
                  {(i.attachments || []).length === 0 ? (
                    <span className="text-zinc-300 text-xs">—</span>
                  ) : (
                    <div className="flex gap-2 items-center" data-testid={`income-docs-${i.income_no}`}>
                      {(i.attachments || []).map((a, idx) => (
                        <a key={a.file_id || a.filename || idx}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.file_id}/download`}
                          target="_blank" rel="noopener noreferrer"
                          title={a.filename}
                          className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-black underline">
                          <Paperclip size={10} /> {a.kind}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
                <td className="p-4 text-right">
                  {canActOn(i) && (
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => approveIncome(i.id)}
                        data-testid={`income-approve-${i.income_no}`}
                        className="text-xs px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-50">
                        Approve
                      </button>
                      <button
                        onClick={() => rejectIncome(i.id)}
                        data-testid={`income-reject-${i.income_no}`}
                        className="text-xs px-2 py-1 border border-rose-600 text-rose-700 hover:bg-rose-50">
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageSize={10} total={totalRecords}
          onPageChange={setPage} testid="income-pagination" />
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><Label className="text-xs uppercase tracking-widest">{label}</Label>{children}</div>
);
